import axios from "axios";
import { IVendor } from "../interfaces/vendor.interface";
// export const fetchVendors = async (params = {}) => {
//   try {
//     return axios
//       .get(`${process.env.REACT_APP_API_URL}/vendor/`, {
//         params: { ...params },
//       })
//       .then((res) => {
//         return res?.data;
//       });
//   } catch (error) {
//     return [];
//   }
// };


const vendorsCache: { [key: string]: { data: any[]; ts: number } } = {};
const vendorsInFlight: { [key: string]: Promise<any[]> | undefined } = {};
const VENDORS_TTL = 60 * 1000;

export const fetchVendors = async (params: any): Promise<any[]> => {
  // Short TTL cache + in-flight dedup: the full vendor list is loaded by several
  // components on the same page mount (e.g. /procurement page + PendingProcurement).
  const key = JSON.stringify(params ?? {});
  const cached = vendorsCache[key];
  if (cached && Date.now() - cached.ts < VENDORS_TTL) {
    return cached.data;
  }
  if (vendorsInFlight[key]) {
    return vendorsInFlight[key] as Promise<any[]>;
  }
  // Use axios (NOT raw fetch) so the global Authorization: JWT ${token}
  // header set in src/index.tsx is applied. Raw fetch sent no auth and the
  // backend returned a 403 JSON ({"detail": "..."}), which the caller then
  // tried to .map() — causing "vendors.map is not a function".
  vendorsInFlight[key] = axios
    .get(`${process.env.REACT_APP_API_URL}/vendor/`, {
      params,
      headers: { Accept: "application/json" },
    })
    .then((response) => {
      const raw = response?.data;
      // Backend may return either a bare list or a paginated {results: [...]}
      // shape. Normalise to an array so the caller can .map() safely.
      const data = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
        ? raw.results
        : [];
      vendorsCache[key] = { data, ts: Date.now() };
      return data;
    })
    .catch((error) => {
      console.error("fetchVendors failed", error);
      return [];
    })
    .finally(() => {
      delete vendorsInFlight[key];
    });
  return vendorsInFlight[key] as Promise<any[]>;
};

export const clearVendorsCache = () => {
  for (const key of Object.keys(vendorsCache)) delete vendorsCache[key];
  for (const key of Object.keys(vendorsInFlight)) delete vendorsInFlight[key];
};

export const addVendor = async (formData: Partial<IVendor>) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/vendor/`,
      formData
    );
    clearVendorsCache();
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const editVendor = async (id: number, data: Partial<IVendor>) => {
  try {
    await axios.put(`${process.env.REACT_APP_API_URL}/vendor/` + id, data);
    clearVendorsCache();
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const getVendor = async (id: number) => {
  try {
    const response = await axios.get<IVendor>(`${process.env.REACT_APP_API_URL}/vendor/` + id);
    return response.data;
  } catch (e) {
    console.error(e);
  }
};

// ---------------------------------------------------------------------------
// Per-vendor cache for GET /vendor/<id>.
//
// The procurement cart renders one ProcurementItemForm per row and each one
// loads its pre-selected vendor (for the warehouse dropdown) on mount. Ten rows
// made ten requests plus ten CORS preflights, repeated on every refetch because
// the forms remount. Rows sharing a vendor now share one in-flight promise, and
// the result is reused across remounts until it expires or a warehouse of that
// vendor is written (putVendorWarehouse / postVendorWarehouse invalidate it).
// ---------------------------------------------------------------------------
const VENDOR_CACHE_TTL_MS = 5 * 60 * 1000;
const vendorCache = new Map<number, { at: number; promise: Promise<IVendor | undefined> }>();

export const invalidateVendorCache = (id?: number | string | null) => {
  if (id === undefined || id === null || id === "") {
    vendorCache.clear();
    return;
  }
  vendorCache.delete(Number(id));
};

export const getVendorCached = (id: number, { force = false } = {}) => {
  const key = Number(id);
  const hit = vendorCache.get(key);
  if (!force && hit && Date.now() - hit.at < VENDOR_CACHE_TTL_MS) return hit.promise;
  const promise = getVendor(key).then((data) => {
    // Don't pin a failed call: the next caller should retry.
    if (!data) vendorCache.delete(key);
    return data;
  });
  vendorCache.set(key, { at: Date.now(), promise });
  return promise;
};

export interface IRawVendorDashboardData {
  vendor: number;
  total_amount: number;
  vendor_details: {
    id: number;
    vendor_code: string;
    name: string;
    contact_person: string | null;
  };
}

export interface IVendorDashboardData {
  vendor: number;
  total_amount: number;
  id: number;
  vendor_code: string;
  name: string;
  contact_person: string | null;
}

export const getVendorDashboardData = async ({
  date_gte,
  date_lte,
}: {
  date_gte: string;
  date_lte: string;
}) => {
  try {
    const response = await axios.get<IRawVendorDashboardData[]>(
      `${process.env.REACT_APP_API_URL}/vendor-dashboard/`,
      {
        params: {
          date_gte,
          date_lte,
        },
      }
    );

    // Flatten the data
    return [
      ...response.data?.map((item) => ({ ...item, ...item.vendor_details })),
    ] as IVendorDashboardData[];
  } catch (e) {
    console.error(e);
  }
};

export const putVendorWarehouse = async (data: any) => {
  try {
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/vendor-warehouse/`, data);
    invalidateVendorCache(data?.vendor);
    return response.data;
  } catch (e) {
    console.error(e);
  }
};

export const postVendorWarehouse = async (data: any) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/vendor-warehouse/`,
      data
    );
    invalidateVendorCache(data?.vendor);
    return response.data;
  } catch (e) {
    console.error(e);
  }
};

export const putVendorOptionalContact = async (data: any) => {
  try {
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/vendor-optional-contacts/`, data);

    return response.data;
  } catch (e) {
    console.error(e);
  }
};

export const postVendorOptionalContact = async (data: any) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/vendor-optional-contacts/`,
      data
    );
    return response.data;
  } catch (e) {
    console.error(e);
  }
};

export const addVendorToZoho = async (vendorId: number) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/vendor-add-to-zoho/`,
      { vendor_id: vendorId }
    );
    return response.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};
